import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Envelope, Lock, User, Ticket } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { apiErr } from "../api";
import { brandLogoFull } from "../brand";
import Disclaimer from "../components/Disclaimer";

export default function Auth() {
  const { login, register, demoLogin } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", username: "", password: "", invite_code: params.get("invite") || "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("demo") === "1") handleDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleDemo = async () => {
    setLoading(true);
    try {
      await demoLogin();
      toast.success("Welcome, FrontierScout");
      nav("/dashboard");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        await register({ ...form, invite_code: (form.invite_code || "").trim() });
        toast.success("Account created. Your expedition begins.");
      } else {
        await login(form.email, form.password);
        toast.success("Welcome back, explorer.");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ff-bg px-4 py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(45,90,39,0.25),transparent_60%)]" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="ff-panel relative w-full max-w-md rounded-md p-8"
      >
        <Link to="/" className="mb-6 flex items-center justify-center">
          <img src={brandLogoFull} alt="Aurumward" className="h-auto w-64 max-w-full object-contain" data-testid="auth-brand-logo" />
        </Link>

        <div className="mb-6 flex rounded-sm border border-ff-brass/30 p-1">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`tab-${m}`}
              onClick={() => setMode(m)}
              className={`flex-1 rounded-sm py-2 text-sm font-medium capitalize transition-colors ${
                mode === m ? "bg-ff-gold text-ff-bg" : "text-ff-muted hover:text-ff-parchment"
              }`}
            >
              {m === "login" ? "Sign In" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field icon={Envelope} testid="email-input" type="email" placeholder="Email" value={form.email} onChange={upd("email")} required />
          {mode === "register" && (
            <Field icon={User} testid="username-input" type="text" placeholder="Username" value={form.username} onChange={upd("username")} required />
          )}
          <Field icon={Lock} testid="password-input" type="password" placeholder="Password" value={form.password} onChange={upd("password")} required />
          {mode === "register" && (
            <div>
              <Field
                icon={Ticket}
                testid="invite-input"
                type="text"
                placeholder="Invite code"
                value={form.invite_code}
                onChange={upd("invite_code")}
                autoComplete="one-time-code"
                autoCapitalize="characters"
                spellCheck={false}
                required
              />
              <p className="mt-1.5 px-1 text-xs text-ff-muted" data-testid="invite-hint">
                Aurumward is in private beta — enter the invite code you were given.
              </p>
            </div>
          )}

          <button
            data-testid="auth-submit-btn"
            disabled={loading}
            className="w-full rounded-sm bg-ff-gold py-3 font-semibold text-ff-bg transition-all duration-200 hover:bg-ff-brass active:scale-95 disabled:opacity-60"
          >
            {loading ? "..." : mode === "register" ? "Begin Expedition" : "Enter"}
          </button>
        </form>

        <div className="my-5 flex items-center gap-3 text-xs text-ff-muted">
          <span className="h-px flex-1 bg-ff-brass/20" /> OR <span className="h-px flex-1 bg-ff-brass/20" />
        </div>

        <button
          data-testid="demo-login-btn"
          onClick={handleDemo}
          disabled={loading}
          className="w-full rounded-sm border border-ff-gold/50 py-3 font-semibold text-ff-gold transition-all duration-200 hover:bg-ff-gold/10 active:scale-95 disabled:opacity-60"
        >
          Play as Demo (FrontierScout)
        </button>

        <div className="mt-6">
          <Disclaimer />
        </div>
      </motion.div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="flex items-center gap-2 rounded-sm border border-ff-brass/30 bg-ff-bg/60 px-3 focus-within:border-ff-gold focus-within:ring-1 focus-within:ring-ff-gold/40">
      <Icon size={18} className="text-ff-brass" />
      <input
        data-testid={testid}
        className="w-full bg-transparent py-3 text-sm text-ff-parchment placeholder:text-ff-muted/60 focus:outline-none"
        {...props}
      />
    </div>
  );
}
